import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, BookOpen, DollarSign, Calendar, Tag } from "lucide-react";
import axios from "axios";

const CreateCoursePage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [course, setCourse] = useState({
    title: "",
    description: "",
    onlinePrice: "",
    offlinePrice: "",
    category: "gpsc",
    duration: "12 months",
    language: "english",
    courseMode: "online",
    location: "",
    features: [],
  });
  const [newFeature, setNewFeature] = useState("");

  const API_BASE_URL =
    process.env.REACT_APP_API_BASE_URL || "http://localhost:5000/api";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(`${API_BASE_URL}/courses`, course, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.data.success) {
        alert("Course created successfully!");
        navigate("/admin/courses");
      }
    } catch (error) {
      console.error("Error creating course:", error);
      setError(error.response?.data?.error || "Failed to create course");
    } finally {
      setLoading(false);
    }
  };

  const addFeature = () => {
    if (newFeature.trim() && !course.features.includes(newFeature.trim())) {
      setCourse({
        ...course,
        features: [...course.features, newFeature.trim()],
      });
      setNewFeature("");
    }
  };

  const removeFeature = (index) => {
    setCourse({
      ...course,
      features: course.features.filter((_, i) => i !== index),
    });
  };

  const categories = [
    { value: "gpsc", label: "GPSC - Gujarat Public Service Commission" },
    { value: "upsc", label: "UPSC - Union Public Service Commission" },
    { value: "Class 3", label: "Class 3" },
  ];

  return (
    <div className="min-h-screen bg-gray-50 pt-32 sm:pt-28 md:pt-24 lg:pt-20">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate("/admin/courses")}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <ArrowLeft size={20} />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Create New Course
                </h1>
                <p className="text-sm text-gray-600">
                  Add a new course to your platform
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Course Information
            </h2>
            <p className="text-sm text-gray-600">
              Fill in the details for your new course
            </p>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Course Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <BookOpen className="inline w-4 h-4 mr-1" />
                Course Title *
              </label>
              <input
                type="text"
                value={course.title}
                onChange={(e) =>
                  setCourse({ ...course, title: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter course title"
                required
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Course Description *
              </label>
              <textarea
                value={course.description}
                onChange={(e) =>
                  setCourse({ ...course, description: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows="4"
                placeholder="Describe what students will learn in this course"
                required
              />
            </div>

            {/* Price Fields - Online and Offline only */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <DollarSign className="inline w-4 h-4 mr-1" />
                  Online Mode Price (₹)
                </label>
                <input
                  type="number"
                  value={course.onlinePrice}
                  onChange={(e) =>
                    setCourse({ ...course, onlinePrice: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Leave empty if not available"
                  min="0"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Leave empty if online mode is not available
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <DollarSign className="inline w-4 h-4 mr-1" />
                  Offline Mode Price (₹)
                </label>
                <input
                  type="number"
                  value={course.offlinePrice}
                  onChange={(e) =>
                    setCourse({ ...course, offlinePrice: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Leave empty if not available"
                  min="0"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Leave empty if offline mode is not available
                </p>
              </div>
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Tag className="inline w-4 h-4 mr-1" />
                Category *
              </label>
              <select
                value={course.category}
                onChange={(e) =>
                  setCourse({ ...course, category: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              >
                {categories.map((cat) => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Duration */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar className="inline w-4 h-4 mr-1" />
                Course Duration
              </label>
              <input
                type="text"
                value={course.duration}
                onChange={(e) =>
                  setCourse({ ...course, duration: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., 12 months, 6 weeks, Self-paced"
              />
            </div>

            {/* Language */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Language
              </label>
              <select
                value={course.language}
                onChange={(e) =>
                  setCourse({ ...course, language: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="gujarati">Gujarati</option>
                <option value="hindi">Hindi</option>
                <option value="english">English</option>
              </select>
            </div>

            {/* Course Mode - Removed, mode will be determined by which prices are set */}
            
            {/* Location - Show always but mark as optional */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Location/Center Address (for offline mode)
              </label>
              <input
                type="text"
                value={course.location}
                onChange={(e) =>
                  setCourse({ ...course, location: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter center location/address"
              />
              <p className="text-xs text-gray-500 mt-1">
                Required only if offline mode is available
              </p>
            </div>

            {/* Course Features */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Course Features
              </label>
              <p className="text-sm text-gray-600 mb-3">
                Add key features and benefits of this course
              </p>

              {/* Add Feature Input */}
              <div className="flex gap-2 mb-3">
                <input
                  type="text"
                  value={newFeature}
                  onChange={(e) => setNewFeature(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Add a course feature"
                  onKeyPress={(e) =>
                    e.key === "Enter" && (e.preventDefault(), addFeature())
                  }
                />
                <button
                  type="button"
                  onClick={addFeature}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  Add
                </button>
              </div>

              {/* Features List */}
              {course.features.length > 0 && (
                <div className="space-y-2">
                  {course.features.map((feature, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded-lg"
                    >
                      <span className="text-sm text-gray-700">{feature}</span>
                      <button
                        type="button"
                        onClick={() => removeFeature(index)}
                        className="text-red-600 hover:text-red-800 text-sm"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Preview Section */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-700 mb-3">
                Course Preview
              </h3>
              <div className="bg-white rounded-lg p-4 border">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold text-gray-900">
                    {course.title || "Course Title"}
                  </h4>
                  <div className="text-sm font-medium text-blue-600">
                    {course.onlinePrice && course.offlinePrice ? (
                      <span>₹{parseInt(course.onlinePrice).toLocaleString()} / ₹{parseInt(course.offlinePrice).toLocaleString()}</span>
                    ) : course.onlinePrice ? (
                      <span>₹{parseInt(course.onlinePrice).toLocaleString()}</span>
                    ) : course.offlinePrice ? (
                      <span>₹{parseInt(course.offlinePrice).toLocaleString()}</span>
                    ) : (
                      <span className="text-gray-400">Price not set</span>
                    )}
                  </div>
                </div>
                <p className="text-sm text-gray-600 mb-2">
                  {course.description || "Course description will appear here"}
                </p>
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                    {categories
                      .find((cat) => cat.value === course.category)
                      ?.label.split(" - ")[0] || "CATEGORY"}
                  </span>
                  <span>{course.duration || "Duration not set"}</span>
                </div>
                {course.features.length > 0 && (
                  <div className="mt-3">
                    <p className="text-xs text-gray-500 mb-1">Features:</p>
                    <ul className="text-xs text-gray-600 space-y-1">
                      {course.features.slice(0, 3).map((feature, index) => (
                        <li key={index} className="flex items-center">
                          <div className="w-1 h-1 bg-green-500 rounded-full mr-2"></div>
                          {feature}
                        </li>
                      ))}
                      {course.features.length > 3 && (
                        <li className="text-gray-500">
                          +{course.features.length - 3} more features
                        </li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-4 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => navigate("/admin/courses")}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "Creating Course..." : "Create Course"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateCoursePage;
